"use client";

import { Pause, Play, X, Loader2 } from "lucide-react";

import { useAudioStore } from "@/modules/audio/store";
import { formatAudioTime, formatAudioTrackTitle } from "@/modules/audio/presentation";

export function GlobalAudioPlayer() {
  const activeTrack = useAudioStore((s) => s.activeTrack);
  const isPlaying = useAudioStore((s) => s.isPlaying);
  const currentTime = useAudioStore((s) => s.currentTime);
  const duration = useAudioStore((s) => s.duration);
  const buffered = useAudioStore((s) => s.buffered);
  const togglePlayback = useAudioStore((s) => s.togglePlayback);
  const seek = useAudioStore((s) => s.seek);
  const playbackRate = useAudioStore((s) => s.playbackRate);
  const cycleSpeed = useAudioStore((s) => s.cycleSpeed);
  const stop = useAudioStore((s) => s.stop);

  if (!activeTrack) return null;

  const totalDuration = duration ?? activeTrack.duration ?? null;
  const progress = totalDuration && totalDuration > 0
    ? Math.max(0, Math.min(100, (currentTime / totalDuration) * 100))
    : 0;
  const bufferProgress = totalDuration && totalDuration > 0
    ? Math.max(0, Math.min(100, (buffered / totalDuration) * 100))
    : 0;
  const isLoading = !isPlaying && currentTime === 0 && buffered === 0 && totalDuration !== null;

  return (
    <div className="border-b border-m-divider bg-m-surface">
      <div className="flex items-center gap-2 px-3 py-1.5">
        <button
          onClick={togglePlayback}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-m-primary text-white"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : isPlaying ? (
            <Pause className="h-3.5 w-3.5" />
          ) : (
            <Play className="h-3.5 w-3.5 ml-0.5" />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-xs font-medium text-m-text">
              {formatAudioTrackTitle(activeTrack.title)}
            </span>
            <span className="shrink-0 text-xs text-m-text-hint tabular-nums">
              {formatAudioTime(currentTime)} / {formatAudioTime(totalDuration)}
            </span>
          </div>
          <div
            className="relative mt-1 h-1 cursor-pointer rounded-full bg-m-surface-light"
            onClick={(e) => {
              if (!totalDuration) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
              seek(fraction * totalDuration);
            }}
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-m-primary/25 transition-[width] duration-300"
              style={{ width: `${bufferProgress}%` }}
            />
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-m-primary transition-[width] duration-150"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <button
          onClick={cycleSpeed}
          className="shrink-0 rounded-md bg-m-surface-light px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-m-primary hover:bg-m-primary/10"
        >
          {playbackRate}x
        </button>

        <button
          onClick={stop}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-m-text-hint hover:text-m-text"
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
